import { Component, computed, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ButtonDirective } from '@innersource/common-ui-components';
import { CapabilityStore } from '../services/capability.store';
import { JobRunHistoryComponent } from '../utils/job-run-history/job-run-history.component';
import { getDisplayName } from '../utils/models/tool.models';

type RecentCapability = {
  key: string;
  name: string;
  routerLink: string;
};

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  imports: [ButtonDirective, RouterLink, JobRunHistoryComponent],
})
export class HomeComponent {
  /** Services. */
  readonly capabilityStore = inject(CapabilityStore);

  /** State. */
  readonly tools = this.capabilityStore.tools;
  readonly toolchains = this.capabilityStore.toolchains;

  /** Returns the accessible capabilities from recent runs. */
  readonly recentCapabilities = computed<RecentCapability[]>(() => {
    const accessibleToolIds = new Set(this.tools.value()?.map(tool => tool.toolId));
    const accessibleToolchainIds = new Set(
      this.toolchains.value()?.map(chain => chain.toolchainId)
    );
    const capabilities = new Map<string, RecentCapability>();

    for (const run of this.capabilityStore.allJobHistory.items()) {
      const isTool = 'toolJobId' in run;
      const referenceId = isTool ? run.tool.toolId : run.toolchain.toolchainId;
      const isAccessible = isTool
        ? accessibleToolIds.has(referenceId)
        : accessibleToolchainIds.has(referenceId);
      const key = `${isTool ? 'tool' : 'toolchain'}:${referenceId}`;

      if (!isAccessible || capabilities.has(key)) continue;

      const capability = isTool
        ? this.capabilityStore.tool(referenceId)
        : this.capabilityStore.toolchain(referenceId);
      if (!capability) continue;

      capabilities.set(key, {
        key,
        name: getDisplayName(capability),
        routerLink: isTool ? `/tools/${referenceId}/run` : `/toolchains/${referenceId}/run`,
      });
    }

    return [...capabilities.values()].slice(0, 3);
  });
}
